import { Ajax } from "../ajax/ajax.js";
import { Table } from "../components/table.js";
import { Browser } from "../core/browser.js";
import { Dialog } from "../dialog/dialog.js";

const ARIA_LABEL_SELECTOR = "a[href], audio, video[controls], input, select, button, textarea, header, footer, nav, main, aside, section, form, [role='navigation'], iframe, img";

export class Razor {
	static #_onStateUpdate = null;
	static #stateUpdated = false;

	static OnThemeChange = null;

	static get OnStateUpdate() {
		return Razor.#_onStateUpdate;
	}

	static set OnStateUpdate(value) {
		if (Razor.#stateUpdated) {
			value?.();
		}

		Razor.#_onStateUpdate = value;
	}

	static GetHandlerURL = handler => {
		const parsed = new URL(document.URL);

		parsed.hash = "";
		parsed.searchParams.set("handler", handler);

		return parsed.toString();
	}

	static async GetPartialView(name, modelProperties, ajaxOptions = {}) {
		modelProperties ??= {};

		const response = await Ajax.Post(this.GetHandlerURL("RenderPartialView"), {
			queryParameters: {
				view: name
			},
			body: modelProperties,
			headers: {
				pragma: 'no-cache',
				'cache-control': 'no-cache'
			},
			success: { 404: () => { } },
			LoadTimeout: 0,
			...ajaxOptions
		});

		return response?.ok ? response.content : "";
	}

	static RenderPartialView(name, modelProperties, container, options = { LoadingIcon: true }) {
		return new Promise(resolve => {
			if (!container) { resolve(); }

			setTimeout(() => {
				if (options.LoadingIcon) {
					container.innerHTML = '<chip-loading class="loading--pv"></chip-loading>';
				}
			}, 300);

			this.GetPartialView(name, modelProperties, options).then(response => {
				options.LoadingIcon = false;
				container.innerHTML = response;

				this.UpdateState();
				resolve();
			});
		});
	}

	static #DisableButton(button) {
		if (button) {
			button.disabled = true;
			button.classList.add("disabled");
		}
	}

	static #EnableButton(button) {
		if (button) {
			button.removeAttribute("disabled");
			button.removeClass("disabled");
		}
	}

	static FireButtonAjax(button) {
		let buttonForm = button.form;

		if (button?.nodeName !== "BUTTON") {
			buttonForm = document.getElementById(button.form) ?? button.closest("form");
		}

		function send() {
			if (!button?.ajax?.SkipFormValidation && (buttonForm && ([...buttonForm.querySelectorAll("chip-form") ?? []].find(x => !x.reportValidity()) || !buttonForm.reportValidity()))) {
				return;
			} else {
				Razor.#DisableButton(button);
			}

			const body = new URLSearchParams();

			if (buttonForm) {
				for (const pair of new FormData(buttonForm)) {
					if (pair[0] !== "__RequestVerificationToken") {
						body.append(pair[0].slice(pair[0].indexOf(".") + 1), pair[1]);
					}
				}
			}

			if (button?.CheckValid?.(body) === false) {
				return Razor.#EnableButton(button);
			}

			const responseOverride = button?.ajax?.success ?? {};

			responseOverride.ok ??= response => {
				history.replaceState("", document.title, button.ajax?.ReturnURL ?? response.url);
				Browser.Refresh();
			}

			responseOverride["400"] ??= response => {
				if (typeof button.ajax?.OnBadRequest === "function") {
					button.ajax.OnBadRequest(response);
				} else {
					Dialog.ShowWarning("That doesn't look right", Razor.GetBadRequestReason(response));
				}
			}

			let url = "";

			if (button.ajax?.Handler) {
				url = Razor.GetHandlerURL(button.ajax.Handler);
			} else {
				url = button.getAttribute("formAction") ? button.formAction : Razor.GetHandlerURL("Save");
			}

			Ajax.Post(url, {
				body,
				OnComplete: response => {
					Razor.#EnableButton(button);

					if (response?.ok) {
						window.onbeforeunload = null;
					}
				},
				success: responseOverride,
				error: error => {
					Razor.#EnableButton(button);

					Dialog.ShowError("Error", Localizer.ERROR_MESSAGE, error);
				}, ...button.ajax
			});
		}

		if (button.dataset.requiresConfirmation === "true") {
			Dialog.ShowConfirmation(button?.ajax?.ConfirmationData?.Title ?? "Are you sure?", button?.ajax?.ConfirmationData?.Body ?? "Are you sure you want to do this?").then(result => {
				if (result) {
					send();
				}
			});
		} else {
			send();
		}
	}

	static GetBadRequestReason = response => {
    if (response.body.reason) { return response.body.reason }
    else { return response.body.at?.(0)?.value.errors.at(0).errorMessage ?? response.content; }
  }

	static UpdateThemeToggleText(theme) {
		if (window.btnThemeToggle && theme) {
			btnThemeToggle.tooltip = Localizer[`THEME_BUTTON_${theme.toUpperCase()}`];
			btnThemeToggle.description = Localizer[`THEME_BUTTON_${theme.toUpperCase()}`];
		}
	}

	static #showTooltip(tooltip, element) {
		if (!tooltip?.trim()) { return; }

		let { width, left, top, height, right } = element.getBoundingClientRect(),
			container = document.createElementWithContents("div",
				`
								<div class="tooltip-content">
									${tooltip}
								</div>
								<div class="tooltip-arrow">
								</div>
							`.trim(),
				{
					className: "tooltip-container",
					id: element.__tooltipID,
					role: "tooltip"
				}
			);

		if (!height && !width) { return; }

		top += window.scrollY;
		right += window.scrollX;
		left += window.scrollX;

		const PLACEMENT_FNS = {
			placeTop: () => {
				Object.assign(container.style, {
					left: `${left + (width / 2)}px`,
					top: `${top}px`
				});

				container.setAttribute("placement", "top");
			},
			placeRight: () => {
				Object.assign(container.style, {
					left: `${left + width}px`,
					top: `${top + (height / 2)}px`
				});

				container.setAttribute("placement", "right");
			},
			placeBottom: () => {
				Object.assign(container.style, {
					top: `${top + height}px`
				});

				container.setAttribute("placement", "bottom");
			},
			placeLeft: () => {
				Object.assign(container.style, {
					left: `${left}px`,
					top: `${top + (height / 2)}px`
				});

				container.setAttribute("placement", "left");
			}
		}

		PLACEMENT_FNS.placeTop();

		document.body.appendChild(container);

		const tooltipDimensions = container.getBoundingClientRect(),
			currentDirection = element.getAttribute("tooltip-direction")?.toLowerCase() ?? "top";

		if (currentDirection !== "top") {
			PLACEMENT_FNS[`place${currentDirection[0].toUpperCase() + currentDirection.slice(1).toLowerCase()}`]();
		}

		if (tooltipDimensions.top <= 0 || tooltipDimensions.left <= 0 || (right + ((tooltipDimensions.width - width) / 2) > window.innerWidth)) {
			if (right + ((tooltipDimensions.width - width) / 2) <= window.innerWidth) {
				PLACEMENT_FNS.placeRight();
			} else if (tooltipDimensions.right <= window.innerWidth) {
				PLACEMENT_FNS.placeBottom();
			} else {
				PLACEMENT_FNS.placeLeft();
			}
		}

		container.classList.add("tooltip--shown");
		element.setAttribute("aria-describedby", element.__tooltipID);
	}

	static RefreshTooltips() {
		document.querySelectorAll("[tooltip]").forEach(element => {
			if (element.__tooltipID) { return; }

			element.__tooltipID ??= `tt${crypto.randomUUID().slice(0, 8)}`;

			let showTimeout,
				hideTimeout;

			if (element.matches?.(ARIA_LABEL_SELECTOR) && !element.hasAttribute("aria-label") && element.textContent.trim() === "") {
				element.ariaLabel = element.getAttribute("tooltip");
			}

			element.onmouseover = ev => {
				const dropdown = ev.target.closest(".dropdown__list"),
					tooltip = element.getAttribute("tooltip") ?? "";

				if (!document.getElementById(element.__tooltipID) && (!dropdown || dropdown.contains(element))) {
					clearTimeout(hideTimeout);
					hideTimeout = null;

					showTimeout ??= setTimeout(() => this.#showTooltip(tooltip, element), 300);
				}
			}

			function hideTooltip() {
				clearTimeout(showTimeout);
				showTimeout = null;

				hideTimeout ??= setTimeout(() => {
					element.removeAttribute("aria-describedby");
					document.getElementById(element.__tooltipID)?.remove();
				}, 300);
			}

			element.onmouseout = ev => {
				const dropdown = ev.target.closest(".dropdown__list");

				if (dropdown?.contains(element) === false || !element.contains(ev.relatedTarget)) {
					hideTooltip();
				}
			}

			element.onmouseleave = () => hideTooltip();
		});
	}

	static InvokeThemeChange() {
		const target = document.querySelector("link[href*='code-highlight']")

		if (target) {
			if (document.documentElement.dataset.theme === "dark") {
				target.href = target.href.replace("code-highlight.css", "code-highlight-dark.css");
			} else {
				target.href = target.href.replace("code-highlight-dark.css", "code-highlight.css");
			}
		}

		Razor.OnThemeChange?.();
	}

	static #RefreshElements() {
		document.querySelectorAll(".standard-pagination-input").forEach(x => x.onkeyup = ev => {
			if (ev.key === "Enter" && !isNaN(ev.target.value) && ev.target.value.trim() !== "") {
				if (!x.classList.contains("table-pagination-input")) {
					Browser.SetURLParameter("p", Math.max(1, parseInt(ev.target.value)), true);
				} else {
					x.closest("chip-grid").previousElementSibling.firstElementChild.Table.GoToPage(Math.max(1, parseInt(ev.target.value)));
				}
			}
		});

		document.querySelectorAll(".btn-collapse-sidebar").forEach(x => x.onclick = () => {
			const nav = x.closest("chip-framesidebar");

			if (nav) {
				nav.collapsed = !nav.collapsed;
			}
		});

		if (window.btnThemeToggle) {
			btnThemeToggle.onclick = () => {
				const theme = document.documentElement.dataset.theme === "dark" ? "light" : "dark";
				Browser.SetColourMode(theme);

				this.UpdateThemeToggleText(theme);
				this.InvokeThemeChange();
			}

			this.UpdateThemeToggleText(document.documentElement.dataset.theme);
		}

		document.querySelectorAll("[data-enhance='true']").forEach(x => x.enhance());

		document.querySelectorAll(".cookie-warning__button--accept").forEach(button => button.onclick = () => {
			document.cookie = `CookiePolicyCheck=null;path=/;expires=${Browser.AddYears(new Date(), 1)}`;
			document.querySelector(".js-cookie-banner").innerHTML = "";
		});

		document.querySelectorAll(".btn-entity-post").forEach(x => x.onclick = () => this.FireButtonAjax(x));

		document.querySelectorAll(".search-bar").forEach(bar => {
			const input = bar.querySelector("input");

			if (input) {
				function submit() {
					if (bar.Search?.href) {
						const url = Object.assign(new URL(Browser.ResolveRelativeURL(bar.Search?.href)), {
							hash: ""
						});

						url.searchParams.set("q", input.value.trim() === "" ? null : input.value.trim());

						location.href = url;
					} else {
						Browser.SetURLParameter("q", input.value.trim() === '' ? null : input.value.trim());
					}
				}

				input.onkeyup = ev => {
					if (ev.key === "Enter") {
						submit();
					}
				}

				bar.querySelector(".search-bar__button").onclick = () => submit();
			}
		});
	}

	static GetVerificationToken() {
		return document.querySelector("input[type='hidden'][name='__RequestVerificationToken']")?.value ?? "";
	}

	static #RefreshComponents() {
		Table.RefreshTables();
	}

	static UpdateState() {
		this.#RefreshComponents();
		this.#RefreshElements();
		this.RefreshTooltips();

		Razor.OnStateUpdate?.();
		Razor.#stateUpdated = true;
	}

	static SetButtonAjax(button, data) {
		if (button) {
			button.ajax ??= {};
			Object.assign(button.ajax, data);
		}
	}
}

Dialog.OnOpened = () => Razor.UpdateState();
